// @mui
import { useTheme } from "@mui/material";
// @nivo
import { ResponsiveChoropleth } from "@nivo/geo";
// coordinates
import coordinates from "../../../../assets/coordinates/features.json";
// hooks
import useResponsive from "~src/hooks/useResponsive";
import {colors} from "~src/data/brandTabColors.ts"

// ---------------------------------------------------------------------------------------------------

// Define a modified version of ChoroplethProps with optional features
interface ModifiedChoroplethProps {
  data: Array<{ id: string; value: number; color?: string }>;
  features?: typeof coordinates.features;
  margin?: { top: number; right: number; bottom: number; left: number };
  colors?: string[];
  domain: number[];
  unknownColor?: string;
  label?: string;
  valueFormat?: string;
  projectionScale?: number;
  projectionRotation?: [number, number, number];
  enableGraticule?: boolean;
  borderWidth?: number;
  borderColor?: string;
  [key: string]: any;
}

// Create a wrapper component that uses default parameters instead of defaultProps
const ChoroplethWrapper = ({
  data,
  features = coordinates.features,
  margin = { top: 0, right: 0, bottom: 0, left: 0 },
  colors,
  domain,
  unknownColor,
  label = "properties.name",
  valueFormat = ".2s",
  projectionScale,
  projectionRotation = [0, 0, 0] as [number, number, number],
  enableGraticule = false,
  borderWidth = 0.5,
  borderColor,
  ...rest
}: ModifiedChoroplethProps) => (
  <ResponsiveChoropleth
    data={data}
    features={features}
    margin={margin}
    colors={colors}
    domain={domain}
    unknownColor={unknownColor}
    label={label}
    valueFormat={valueFormat}
    projectionScale={projectionScale}
    projectionRotation={projectionRotation}
    enableGraticule={enableGraticule}
    borderWidth={borderWidth}
    borderColor={borderColor}
    {...rest}
  />
);

export default function GeoGraph({
  graphData,
}: {
  graphData: { id: string; value: number }[][];
}) {
  const theme = useTheme();
  const isDesktop = useResponsive("up", "lg");
  const combinedData = graphData.flatMap((array, outerIndex) =>
    array.map((item) => ({
      ...item,
      color: colors[outerIndex % colors.length],
    })),
  );
  const data = combinedData;
  if (!combinedData) {
    return null;
  }

  return (
    <ChoroplethWrapper
      data={data}
      colors={data.map((item) => item.color)}
      domain={[1, 100]}
      unknownColor={theme.palette.grey[100]}
      projectionScale={isDesktop ? 90 : 50}
      borderColor={theme.palette.grey[400]}
    />
  );
}
